import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "../lib/site";

const LINKS = [
  { id: "process", label: "Process" },
  { id: "collections", label: "Collections" },
  { id: "estimator", label: "Estimator" },
  { id: "reviews", label: "Reviews" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-sand-border shadow-[0_8px_30px_rgba(134,95,52,0.06)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label="Reef Deco — back to top"
        >
          <img src="/logo.png" alt="Reef Deco — Curtains | Blinds | Interiors" className="h-9 sm:h-10 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-600 hover:text-reef transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-600 hover:text-reef transition-colors duration-200"
          >
            Blog
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/estimate-calculator"
            data-testid="nav-calculator-btn"
            className="px-5 py-2.5 rounded-full border border-reef/30 text-reef font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-light transition-colors duration-200"
          >
            Get Estimate
          </Link>
          <Link
            to="/book-free-visit"
            data-testid="nav-book-visit-btn"
            className="px-5 py-2.5 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors duration-200 flex items-center gap-2"
          >
            <CalendarCheck className="w-3.5 h-3.5" />
            Book a Free Visit
          </Link>
        </div>

        <button
          data-testid="nav-mobile-menu-btn"
          className="lg:hidden text-reef p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-sand-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-mono text-xs tracking-[0.2em] uppercase text-slate-700"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/blog"
                data-testid="nav-mobile-link-blog"
                onClick={() => setOpen(false)}
                className="font-mono text-xs tracking-[0.2em] uppercase text-slate-700"
              >
                Blog
              </Link>
              <Link
                to="/estimate-calculator"
                data-testid="nav-mobile-estimate-btn"
                onClick={() => setOpen(false)}
                className="mt-2 px-5 py-3 rounded-full border border-reef/30 text-reef text-center font-mono text-xs tracking-[0.15em] uppercase"
              >
                Get Estimate
              </Link>
              <Link
                to="/book-free-visit"
                data-testid="nav-mobile-book-btn"
                onClick={() => setOpen(false)}
                className="px-5 py-3 rounded-full bg-reef text-sand-bg text-center font-mono text-xs tracking-[0.15em] uppercase"
              >
                Book a Free Visit
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
